import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X, ListChecks } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

interface FilterOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: readonly FilterOption[];
  selected: string[];
  onSelectionChange: (values: string[]) => void;
  renderOption?: (option: FilterOption) => React.ReactNode;
  /** Renders the pill greyed and unopenable — used whenever the filter cannot apply. */
  disabled?: boolean;
  /** Why it is disabled. Shown as a native tooltip on the WRAPPER, see below. */
  disabledHint?: string;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onSelectionChange,
  renderOption,
  disabled = false,
  disabledHint,
}: MultiSelectFilterProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const hasSelection = selected.length > 0;
  const allSelected = options.length > 0 && selected.length === options.length;

  function toggleValue(value: string) {
    if (selected.includes(value)) {
      onSelectionChange(selected.filter((v) => v !== value));
    } else {
      onSelectionChange([...selected, value]);
    }
  }

  return (
    <Popover open={open && !disabled} onOpenChange={(next) => !disabled && setOpen(next)}>
      {/*
        The hint lives on this span, NOT on the button: a disabled button fires no mouse events,
        so a `title` on it would never show a tooltip in Chrome or Safari. The span is not
        disabled, so it does.
      */}
      <span title={disabled ? disabledHint : undefined} className="inline-flex">
        <PopoverTrigger asChild>
          <button
            disabled={disabled}
            className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              disabled
                ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                : hasSelection
                  ? 'border-brand-rose/40 bg-brand-rose/5 text-brand-rose'
                  : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
            }`}
          >
            {label}
            {/*
              The count shows even while disabled. A narrowing filter must never be invisible:
              a hand-written URL can arrive with a value and no ticket type pinned, and the
              user's way out is the chip below, not this pill.
            */}
            {hasSelection && (
              <span
                className={`ml-0.5 flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-bold ${
                  disabled ? 'bg-slate-400' : 'bg-brand-rose'
                }`}
              >
                {selected.length}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 ${disabled ? 'text-slate-300' : 'text-slate-400'}`} />
          </button>
        </PopoverTrigger>
      </span>
      <PopoverContent className="w-56 p-2" align="start">
        {/*
          Capped and scrollable: 12 order subcategories at ~30px overflow a laptop viewport and
          push the footer out of reach. The footer stays OUTSIDE the scroller.
        */}
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggleValue(opt.value)}
              />
              {renderOption ? renderOption(opt) : (
                <span className="text-sm text-slate-700">{opt.label}</span>
              )}
            </label>
          ))}
        </div>
        {/*
          "Select all" is what makes a long, declaration-ordered list usable: on production one
          order subcategory is 46% of the rows, so the question people actually have is
          "everything except that one" — two clicks here instead of eleven.
        */}
        {options.length > 0 && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
            {!allSelected && (
              <button
                onClick={() => onSelectionChange(options.map((o) => o.value))}
                className="flex items-center gap-1 flex-1 px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-brand-rose rounded-md hover:bg-slate-50 transition-colors"
              >
                <ListChecks className="w-3 h-3" />
                {t('actions.selectAll')}
              </button>
            )}
            {hasSelection && (
              <button
                onClick={() => onSelectionChange([])}
                className="flex items-center gap-1 flex-1 px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-brand-rose rounded-md hover:bg-slate-50 transition-colors"
              >
                <X className="w-3 h-3" />
                {t('actions.clearAll')}
              </button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
